import { ActionButton } from "@via/components/action-button/action-button.tsx";
import Drawer from "@via/components/drawer.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { useGetSavedCredentials } from "@via/pages/account/profile/profile.api.tsx";
import RunEnvironmentForm from "@via/pages/account/profile/run-environments/run-environment-form/run-environment-form.tsx";
import RunEnvironmentsTable from "@via/pages/account/profile/run-environments/table/run-environments-table.tsx";
import { BUTTONS, USER_ROLES } from "@via/utils/constants.tsx";
import { CredentialView, type ProfileClusterForClient } from "common";
import { type JSX, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { environmentIdFromSearch } from "@via/pages/account/profile/run-environments/environment-deep-link.ts";

export default function RunEnvironments(): JSX.Element {
  const { user } = useAuth();
  // agent-4-2 (spec A4). `?environment=<id>` opens that environment at its Agent card. The table owns the
  // edit drawer and decides whether the id is one of its rows.
  const [searchParams] = useSearchParams();
  const focusEnvironmentId = environmentIdFromSearch(searchParams);

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isPublicDrawerOpen, setPublicDrawerOpen] = useState(false);
  const [runEnvironmentFormKey, setRunEnvironmentFormKey] = useState(1);
  const [selectedPublicTemplate, setSelectedPublicTemplate] =
    useState<ProfileClusterForClient>();

  useEffect(() => {
    setRunEnvironmentFormKey(prev => prev + 1);
    setSelectedPublicTemplate(undefined);
  }, [isDrawerOpen]);

  const { data: savedCredentials, isLoading: isSavedCredentialsLoading } =
    useGetSavedCredentials({ type: CredentialView.UserOwned });

  const {
    data: publicSavedCredentials,
    isLoading: isPublicSavedCredentialsLoading,
  } = useGetSavedCredentials({ type: CredentialView.Public });

  const runEnvironments = savedCredentials?.profiles || [];
  const publicRunEnvironments = publicSavedCredentials?.profiles || [];

  return (
    <div>
      <div className="p-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:w-full sm:rounded-lg">
        <div className="via-page-heading-wrapper">
          <h2 className="via-h2">Run Environments</h2>

          <ActionButton
            button={BUTTONS.CREATE_ENVIRONMENT}
            onClick={() => {
              setDrawerOpen(true);
            }}
          />
        </div>

        {isSavedCredentialsLoading ? (
          <div className="my-4">
            <SkeletonLoader count={12} />
          </div>
        ) : (
          <RunEnvironmentsTable
            runEnvironments={runEnvironments}
            focusEnvironmentId={focusEnvironmentId}
          />
        )}

        <Drawer
          id="run-environments"
          open={isDrawerOpen}
          setOpen={setDrawerOpen}>
          <RunEnvironmentForm
            setOpen={setDrawerOpen}
            publicRunEnvironments={publicRunEnvironments}
            editableRunEnvironment={selectedPublicTemplate}
            setEditableRunEnvironment={setSelectedPublicTemplate}
            key={runEnvironmentFormKey}
          />
        </Drawer>
      </div>

      {user.userRole === USER_ROLES.ADMIN ? (
        <div className="mt-6 p-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:w-full sm:rounded-lg">
          <div className="via-page-heading-wrapper">
            <h2 className="via-h2">Public Run Environment Templates</h2>

            <ActionButton
              button={BUTTONS.CREATE_PUBLIC_ENVIRONMENT}
              onClick={() => {
                setPublicDrawerOpen(true);
              }}
            />
          </div>

          {isPublicSavedCredentialsLoading ? (
            <div className="my-4">
              <SkeletonLoader count={12} />
            </div>
          ) : (
            <RunEnvironmentsTable
              runEnvironments={publicRunEnvironments}
              isPublic
            />
          )}

          <Drawer
            id="run-environments"
            open={isPublicDrawerOpen}
            setOpen={setPublicDrawerOpen}>
            <RunEnvironmentForm setOpen={setPublicDrawerOpen} isPublic />
          </Drawer>
        </div>
      ) : null}
    </div>
  );
}
